import React, { useEffect, useState } from 'react';
import { Modal, Select, Alert, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { TRAINING_LANGUAGES, MAX_LANGUAGES_PER_REQUEST } from '../../utils/trainingModel';

const { Text } = Typography;

/**
 * AI-translate every reading/quiz module into the chosen Indian languages
 * (POST .../courses/:courseId/translate). Each (module, language) lands as `pending_review`;
 * a human must approve every one before that language becomes selectable by a learner
 * (per-language publish gating, Commandment IV). Advisor / Lead Advisor only (server-enforced).
 */
const TranslateModal = ({ open, projectId, courseId, onClose, onDone, onPartial }) => {
    const { request } = useApiRequest();
    const [languages, setLanguages] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setLanguages([]);
            setError(null);
        }
    }, [open]);

    const handleOk = async () => {
        if (languages.length === 0) return setError('Choose at least one language.');
        setBusy(true);
        setError(null);
        try {
            const { data } = await request('POST', `/projects/${projectId}/training/courses/${courseId}/translate`, {
                body: { targetLanguages: languages },
            });
            const failed = (data.failed || []).length;
            message.success(`${(data.translations || []).length} translation(s) drafted - review each one.`);
            if (failed > 0) message.warning(`${failed} module translation(s) failed and were skipped. Running Translate again also resets approved translations in that language to pending.`);
            onDone();
        } catch (err) {
            setError((err.message || 'Translation failed.') + ' Some translations may already have been saved - close this dialog and check the module cards.');
            if (onPartial) onPartial();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Translate course content"
            okText="Translate with AI"
            confirmLoading={busy}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Alert
                    type="info"
                    showIcon
                    message="Translations are AI drafts"
                    description="Reading and quiz text is translated; video subtitles are not generated. A language becomes selectable for learners once every module in it is approved, and only while the source text is unchanged (editing a module resets its translations). Running Translate again for a language resets its translations to pending review, including ones you already approved. This can take a minute per language."
                />
                <div>
                    <Text strong>Languages (up to {MAX_LANGUAGES_PER_REQUEST} at a time)</Text>
                    <Select
                        aria-label="Languages"
                        mode="multiple"
                        maxCount={MAX_LANGUAGES_PER_REQUEST}
                        style={{ width: '100%' }}
                        value={languages}
                        onChange={setLanguages}
                        options={TRAINING_LANGUAGES}
                        placeholder="Select target languages"
                    />
                </div>
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

export default TranslateModal;
